// The instrument's two building blocks: a numbered panel with an engraved
// header, and a label/value readout row.
import { cn } from "@bindery/ui/lib/utils";
import type { ReactNode } from "react";

export function Panel({
  index,
  title,
  aside,
  className,
  children,
}: {
  index: string;
  title: string;
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className={cn(
        "bg-card text-card-foreground flex min-w-0 flex-col rounded-sm border shadow-[0_1px_0_var(--border),0_8px_24px_-18px_oklch(0.2_0.01_70/0.35)]",
        className,
      )}
    >
      <header className="flex min-h-9 items-center justify-between gap-3 border-b px-3 py-1.5">
        <h2 className="lab-label">
          <span className="text-foreground/80 mr-2">{index}</span>
          {title}
        </h2>
        {aside}
      </header>
      <div className="flex flex-1 flex-col gap-3 p-3">{children}</div>
    </section>
  );
}

export function Readouts({ children }: { children: ReactNode }) {
  return (
    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
      {children}
    </dl>
  );
}

export function Readout({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <>
      <dt className="lab-label self-center">{label}</dt>
      <dd className="lab-readout min-w-0 break-words">{children}</dd>
    </>
  );
}

// A value that is honestly absent, drawn so it is not mistaken for "0" or "".
export function Absent({ children = "—" }: { children?: ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}
